import useSWR from "swr"
import { format, parseISO } from "date-fns"
import { CheckCircle2, FilePlus2, Loader2, Pencil, Trash2 } from "lucide-react"
import { api_fetch_throws } from "../../../../api/client"
import {
  PromotionAuditEvent,
  format_change_value,
  format_field_label,
} from "../promotion_history_helpers"

interface PromotionHistoryTabProps {
  promotion_id: string
}

function event_icon(event_type: string) {
  switch (event_type) {
    case "created":
      return <FilePlus2 className="h-4 w-4 text-green-600" />
    case "stage_changed":
      return <CheckCircle2 className="h-4 w-4 text-blue-600" />
    case "deleted":
      return <Trash2 className="h-4 w-4 text-red-600" />
    default:
      return <Pencil className="h-4 w-4 text-gray-500" />
  }
}

/** Running change history for a promotion: who did what, when, with before/after values. */
export function PromotionHistoryTab({ promotion_id }: PromotionHistoryTabProps) {
  const { data: events, error, isLoading } = useSWR(`/promotions/${promotion_id}/audit`, (url: string) =>
    api_fetch_throws<PromotionAuditEvent[]>(url),
  )

  if (isLoading) {
    return (
      <div class="flex items-center justify-center py-12 text-gray-500">
        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
        Loading history...
      </div>
    )
  }

  if (error) {
    return <div class="py-12 text-center text-sm text-red-600">Failed to load history</div>
  }

  if (!events || events.length === 0) {
    return (
      <div class="py-12 text-center text-sm text-gray-500">
        No history recorded yet. Changes made from now on are logged here with who made them and what changed.
      </div>
    )
  }

  return (
    <ol class="space-y-4">
      {events.map(event => (
        <li key={event.id} class="flex gap-3">
          <div class="mt-0.5 shrink-0">{event_icon(event.event_type)}</div>
          <div class="min-w-0 flex-1">
            <div class="flex flex-wrap items-baseline gap-x-2">
              <span class="text-sm font-medium text-gray-900">{event.summary}</span>
              <span class="text-xs text-gray-500">
                {event.actor_name || "System"} · {format(parseISO(event.created_at), "MMM d, yyyy h:mm a")}
              </span>
            </div>
            {event.changes && event.changes.length > 0 && (
              <div class="mt-1.5 overflow-x-auto">
                <table class="text-xs">
                  <tbody>
                    {event.changes.map(change => (
                      <tr key={change.field}>
                        <td class="pr-3 py-0.5 font-medium text-gray-600 whitespace-nowrap align-top">
                          {format_field_label(change.field)}
                        </td>
                        <td class="pr-2 py-0.5 text-gray-500 line-through align-top">{format_change_value(change.from)}</td>
                        <td class="pr-1 py-0.5 text-gray-400 align-top">→</td>
                        <td class="py-0.5 text-gray-900 align-top">{format_change_value(change.to)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </li>
      ))}
    </ol>
  )
}
